'use client'

import { useTranslations } from '@/lib/next-shim'
import type React from 'react'
import dayjs from 'dayjs'
import { useWatch } from 'react-hook-form'

import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'

import { Gender } from '@/core/graphql/types'
import { ControlledDateField, ControlledSelectField, ControlledTextField } from '@/shared/ui/rhform-controls'

import type { SettingsFormData } from '../model/profileForms'
import { useProfileMainCard } from '../model/useProfileMainCard'
import { IdentityChangeOtpDialog } from './IdentityChangeOtpDialog'
import { ReadOnlyField } from './ReadOnlyField'
import { SaveButton } from './SaveButton'

const EMPTY = '—'

function display(value: unknown): string {
  return value === undefined || value === null || value === '' ? EMPTY : String(value)
}

function displayDate(value: unknown): string {
  if (!value) return EMPTY
  const date = value instanceof Date ? dayjs(value) : dayjs(String(value))
  return date.isValid() ? date.format('DD/MM/YYYY') : EMPTY
}

function SubSectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
      {icon}
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
        {title}
      </Typography>
    </Stack>
  )
}

export function ProfileMainCard() {
  const t = useTranslations()
  const { control, identityOtp, loading, mode, onIdentityOtpClose, onIdentityOtpConfirmed, onMainInfoSubmit, onRequestEdit, requestingEdit } =
    useProfileMainCard()

  const readOnly = mode === 'read'
  const values = useWatch({ control })

  const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } as const
  const dashedSection = { pt: 4, borderTop: 1, borderColor: 'divider', borderStyle: 'dashed' } as const

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden', boxShadow: '0 12px 40px rgba(25,28,29,0.06)' }}>
      {/* Card header: title + edit button (read mode) */}
      <Box
        sx={{
          px: 4,
          py: 2.5,
          borderBottom: '1px dashed',
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PersonOutlineIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('page.AccountSettings.profile.sections.personalInfo')}
          </Typography>
        </Stack>
        {readOnly && (
          <Button
            type="button"
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={onRequestEdit}
            disabled={requestingEdit}
            sx={{ borderRadius: 2, whiteSpace: 'nowrap', px: 2 }}
          >
            {t('page.AccountSettings.profile.editButton')}
          </Button>
        )}
      </Box>

      <Box component="form" data-testid="profile-main-form" onSubmit={onMainInfoSubmit} sx={{ p: 4 }}>
        <Stack spacing={4}>
          {/* Personal information */}
          <Box sx={gridSx}>
            {readOnly ? (
              <>
                <ReadOnlyField label={t('field.name.label')} value={display(values.name)} />
                <ReadOnlyField label={t('field.lastname.label')} value={display(values.surname)} />
                <ReadOnlyField label={t('field.birthday.label')} value={displayDate(values.birthday)} />
                <ReadOnlyField label={t('field.gender.label')} value={display(values.gender)} />
              </>
            ) : (
              <>
                <ControlledTextField<SettingsFormData> name="name" label={t('field.name.label')} control={control} autoComplete="given-name" />
                <ControlledTextField<SettingsFormData> name="surname" label={t('field.lastname.label')} control={control} autoComplete="family-name" />
                <ControlledDateField<SettingsFormData> name="birthday" label={t('field.birthday.label')} control={control} />
                <ControlledSelectField<SettingsFormData>
                  name="gender"
                  label={t('field.gender.label')}
                  control={control}
                  options={Object.values(Gender).map((gender) => ({ value: gender, label: gender }))}
                />
              </>
            )}
          </Box>

          {/* Contact information */}
          <Box sx={dashedSection}>
            <SubSectionHeader
              icon={<MailOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />}
              title={t('page.AccountSettings.profile.sections.contact')}
            />
            <Box sx={gridSx}>
              {readOnly ? (
                <>
                  <ReadOnlyField label={t('field.email.label')} value={display(values.email)} />
                  <ReadOnlyField label={t('field.phone.label')} value={display(values.phone)} />
                </>
              ) : (
                <>
                  <ControlledTextField<SettingsFormData> name="email" label={t('field.email.label')} type="email" control={control} autoComplete="email" />
                  <ControlledTextField<SettingsFormData> name="phone" label={t('field.phone.label')} type="tel" control={control} autoComplete="tel" />
                </>
              )}
            </Box>
          </Box>

          {/* Location */}
          <Box sx={dashedSection}>
            <SubSectionHeader
              icon={<LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />}
              title={t('page.AccountSettings.profile.sections.location')}
            />
            <Stack spacing={3}>
              {readOnly ? (
                <ReadOnlyField label={t('field.adress.label')} value={display(values.street)} />
              ) : (
                <ControlledTextField<SettingsFormData> name="street" label={t('field.adress.label')} control={control} autoComplete="street-address" />
              )}
              <Box sx={gridSx}>
                {readOnly ? (
                  <>
                    <ReadOnlyField label={t('field.city.label')} value={display(values.city)} />
                    <ReadOnlyField label={t('field.postalCode.label')} value={display(values.cp)} />
                  </>
                ) : (
                  <>
                    <ControlledTextField<SettingsFormData> name="city" label={t('field.city.label')} control={control} autoComplete="address-level2" />
                    <ControlledTextField<SettingsFormData> name="cp" label={t('field.postalCode.label')} control={control} autoComplete="postal-code" />
                  </>
                )}
              </Box>
            </Stack>
          </Box>

          {!readOnly && (
            <Box sx={{ pt: 2 }}>
              <SaveButton label={t('button.saveChanges.label')} loading={loading} />
            </Box>
          )}
        </Stack>
      </Box>

      <IdentityChangeOtpDialog open={identityOtp.open} sentTo={identityOtp.sentTo} onClose={onIdentityOtpClose} onConfirmed={onIdentityOtpConfirmed} />
    </Paper>
  )
}
